import { Component, ElementRef, HostListener, Input, ViewChild, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { MatTableDataSource } from '@angular/material/table';
import * as _ from 'underscore';

@Component({
  selector: 'app-interventions-table',
  templateUrl: './interventions-table.component.html',
  styleUrls: ['./interventions-table.component.scss'],
  providers: [ConfirmationService],
})
export class InterventionsTableComponent {
  private editCellMap = { id: undefined, old_value: undefined, field: undefined };
  public applyDisabled = true;
  rowGroupField = 'intervention_name';

  all_filters = [];
  selectedInterventions = []; // can be used while deleting in bulk

  // Table
  @Input() tableColumns = []; //input
  @Input() visibleColumns: string[] = [];
  @Input() tableData = []; //input
  frozenColStartIndex = 0;
  @Input() frozenColEndIndex = 5;
  @Output() deleteIntervention = new EventEmitter();
  @Output() editIntervention = new EventEmitter();
  @Output() dataUpdated = new EventEmitter();
  originalTableData: any;
  editedCells: any[] = [];
  dataChanged: boolean = false;
  @Input() editMode: { [key: string]: { [key: string]: boolean } } = {};

  selectedCells: Set<{ rowIndex: number; columnIndex: number }> = new Set();
  copiedValue: any = null;
  isDragging: boolean = false; // Tracks drag state
  dragStartCell: { rowIndex: number; columnIndex: number } | null = null;

  @ViewChild('editInput') editInput!: ElementRef;

  public dataSource = new MatTableDataSource();
  isTableDataReady: boolean = false;
  constructor(private confirmationService: ConfirmationService) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.tableColumns.length > 0 && this.tableData.length > 0 && this.visibleColumns.length > 0) {
      this.isTableDataReady = true;
    } else {
      this.isTableDataReady = false;
    }
  }

  public onCellEditInit(event) {
    if (this.editCellMap.id) {
      this.editMode[this.editCellMap.id][this.editCellMap.field] = false;
    }
    this.editCellMap = {
      id: undefined,
      old_value: undefined,
      field: undefined,
    };
    this.editCellMap.id = event.data.id;
    this.editCellMap.field = event.field;
    this.editCellMap.old_value = event.data[event.field];
    this.editMode[event.data.id][event.field] = true;
    // console.log('Edit Init: ', this.editCellMap);
    setTimeout(() => {
      if (this.editInput) {
        this.editInput.nativeElement.focus();
      }
    }, 100);
  }

  public onCellEditCancel(event) {
    const edited_row = event.data;
    edited_row[event.field].data = this.editCellMap.old_value;
    this.editMode[event.data.id][event.field] = false;
  }

  onCellEditComplete(event) {
    // console.log('Cell Edit Complete', event);
    if (!event.data) {
      this.editMode[event.data.id][event.field] = false;
      return;
    }
    if (this.editCellMap.old_value == event.data[event.field]) {
      this.editMode[event.data.id][event.field] = false;
      // event.data[event.field + '_dirty'] = false;
      // event.data['dirty'] = false;
      return;
    }
    if (event.data[event.field] != 0 && (event.data[event.field] == '' || event.data[event.field] == null)) {
      event.data[event.field] = this.editCellMap.old_value;
      this.editMode[event.data.id][event.field] = false;
      return;
    }

    event.data[event.field + '_dirty'] = true;
    event.data['dirty'] = true;
    this.editMode[event.data.id][event.field] = false;

    this.applyDisabled = false;

    // Add Logic to change inter related cells
    if (event.data['properties'] === 'Additional Capacity (lbs)') {
      this.handleAdditionalCapacityChange(event.rowIndex, event.columnIndex);
    }

    // if (event.data['properties'] === 'Incremental Throughput (lbs/shift)') {
    if (event.data['properties'] === 'Incremental Throughput (lbs/shift)') {
      this.handleThroughputChange(event.rowIndex, event.columnIndex);
    }

    if (event.data['properties'] === 'Total Line Shifts') {
      // if (event.data['properties'] === 'Shifts') {
      this.handleShiftsChange(event.rowIndex, event.columnIndex);
    }

    if (event.data['properties'] === 'CAPEX Requirement ($)') {
      // if (event.data['properties'] === 'Shifts') {
      this.handleCapexChange(event.rowIndex, event.columnIndex);
    }

    this.getUpdatedRecords();
  }

  getUpdatedRecords() {
    const updatedRecords = this.tableData.filter((el) => el.dirty);
    if (updatedRecords.length == 0) return;
    this.editedCells = [];
    this.editedCells = _.flatten(
      _.map(updatedRecords, (item) => {
        // Get all keys ending with '_dirty' and marked as true
        let dirtyFields = _.filter(_.keys(item), (key) => key.endsWith('_dirty') && item[key] === true);
        if (dirtyFields.length == 0) {
          dirtyFields = _.keys(item)
            .filter((key) => /^Q[1-4]-\d{4}$/.test(key))
            .slice(0, 1);
        }
        // Form the edited cell objects for each dirty field
        return _.map(dirtyFields, (dirtyKey) => {
          let field = dirtyKey.replace('_dirty', ''); // Get the corresponding field
          return {
            iteration_code: item.iteration_code,
            intervention_id: item.intervention_id,
            intervention_name: item.intervention_name,
            manufacturing_network: item.manufacturing_network,
            line_code: item.line_code,
            properties: item.properties,
            format_type: item.format_type,
            scope: item.scope,
            field: field,
            value: item[field], // Get the value for the dirty field
            is_active: item.is_active,
          };
        });
      })
    );

    this.dataUpdated.emit({
      editedCells: this.editedCells,
      applyDisabled: this.applyDisabled,
    });
  }

  // Show the cell only for the first occurrence
  visibleGroup(id: number, data: string, index: number, field: string): boolean {
    const firstIndex = this.tableData.findIndex((item) => item.intervention_id == id && item[field] === data);
    const count = this.tableData.filter((item) => item.intervention_id == id && item[field] === data).length;
    return firstIndex + Math.floor(count / 2) - 1 == index;
  }

  showTopBorder(id: number, data: string, index: number, field: string): boolean {
    return this.tableData.findIndex((item) => item.intervention_id == id && item[field] === data) == index;
  }

  showBottomBorder(id: number, data: string, index: number, field: string): boolean {
    const count = this.tableData.filter((item) => item.intervention_id == id && item[field] === data).length;
    const firstIndex = this.tableData.findIndex((item) => item.intervention_id == id && item[field] === data);
    return firstIndex + count - 1 == index;
  }

  onMouseDown(rowIndex: number, columnIndex: number): void {
    this.isDragging = true;
    this.dragStartCell = { rowIndex, columnIndex };
    this.selectedCells.clear(); // Clear previous selection
    this.selectedCells.add(this.dragStartCell);
    this.selectCells(rowIndex, columnIndex, rowIndex, columnIndex); // Start selection
  }

  onMouseMove(event, rowIndex: number, columnIndex: number): void {
    if (!this.isDragging || !this.dragStartCell) return;
    event.preventDefault();
    // Select the rectangle of cells between the start and current cell
    this.selectCells(this.dragStartCell.rowIndex, this.dragStartCell.columnIndex, rowIndex, columnIndex);
  }

  onMouseUp(): void {
    this.isDragging = false;
    this.dragStartCell = null; // Reset start cell
  }

  // Select a 2D range of cells
  selectCells(startRow: number, startCol: number, endRow: number, endCol: number): void {
    this.selectedCells.clear();

    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        this.selectedCells.add({ rowIndex: row, columnIndex: col });
      }
    }
  }

  // Check if a cell is selected
  isCellSelected(rowIndex: number, columnIndex: number): boolean {
    return [...this.selectedCells].some((cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex);
  }

  @HostListener('document:keydown', ['$event'])
  handleCopyPaste(event: KeyboardEvent): void {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    if ((event.key === 'c' || event.key === 'C') && (event.ctrlKey || (isMac && event.metaKey))) {
      this.copySelectedCells();
    }

    if ((event.key === 'v' || event.key === 'V') && (event.ctrlKey || (isMac && event.metaKey))) {
      // event.preventDefault(); // Prevent default paste behavior
      this.pasteToSelectedCells(event);
    }
    if (event.key === 'Tab') {
      this.handleTabNavigation(event);
    }
    if (event.key === 'Escape') {
      this.selectedCells.clear();
      this.isDragging = false;
      this.dragStartCell = null;
    }
  }

  handleTabNavigation(event) {
    const { id, field } = this.editCellMap;
    if (!id || !field) {
      return;
    }
    event.preventDefault();
    const currentColumnIndex = this.tableColumns.map((el) => el.field).indexOf(field);
    this.onCellEditComplete({ field, data: this.tableData[id - 1], rowIndex: id, columnIndex: currentColumnIndex });
    // Determine the next cell
    let nextRowIndex = id - 1;
    let nextColumnIndex = currentColumnIndex + 1;

    // If we're at the last column, move to the next row
    if (nextColumnIndex >= this.tableColumns.length) {
      nextRowIndex += 1;
      nextColumnIndex = 0;
    }

    // If we're at the last row, stop navigation
    if (nextRowIndex >= this.tableData.length) {
      return;
    }

    const nextColumnKey = this.tableColumns[nextColumnIndex].field;
    this.onCellEditInit({ field: nextColumnKey, data: this.tableData[nextRowIndex] });
  }

  // Copy the value(s) of the selected cell(s)
  copySelectedCells(): void {
    if (this.selectedCells.size === 0) {
      console.warn('No cells selected for copying!');
      return;
    }

    // const copiedValues = [...this.selectedCells].map(({ rowIndex, columnIndex }) => this.tableData[rowIndex - 1][this.displayedColumns[columnIndex].field]).join('\n'); // Use newline to separate values if multiple cells are selected.
    const copiedValues = [...this.selectedCells]
      .slice(0, 1)
      .map(({ rowIndex, columnIndex }) => this.tableData[rowIndex - 1][this.tableColumns[columnIndex].field])
      .join('\n'); // Use newline to separate values if multiple cells are selected.

    if (parseFloat(copiedValues) || parseFloat(copiedValues) == 0) {
      this.copiedValue = parseFloat(copiedValues);

      // Copy to clipboard using the Clipboard API
      navigator.clipboard.writeText(this.copiedValue).then(
        () => console.log('Copied to clipboard:', this.copiedValue),
        (error) => console.error('Failed to copy to clipboard:', error)
      );
    }
    this.selectedCells.clear();
  }

  // Paste the copied value into all selected cells
  pasteToSelectedCells(event): void {
    navigator.clipboard.readText().then((clipboardText) => {
      console.log('clipboardText :>> ', clipboardText);
      if (!clipboardText || clipboardText == '' || this.selectedCells.size === 0 || Number.isNaN(parseFloat(clipboardText.trim()))) {
        console.warn('No cells selected for pasting!');
        return;
      }
      event.preventDefault();

      [...this.selectedCells].forEach(({ rowIndex, columnIndex }) => {
        if (this.tableColumns[columnIndex].editable) {
          const columnKey = this.tableColumns[columnIndex].field;
          this.onCellEditInit({
            field: columnKey,
            data: this.tableData[rowIndex - 1],
          });
          const pastedValue = parseFloat(clipboardText.trim());
          if (pastedValue || pastedValue == 0) {
            this.tableData[rowIndex - 1][columnKey] = pastedValue;
            // this.tableData[rowIndex - 1][columnKey + '_dirty'] = true;
            // this.tableData[rowIndex - 1]['dirty'] = true;
          }

          // Call Cell Edit COmplete
          this.onCellEditComplete({
            field: columnKey,
            data: this.tableData[rowIndex - 1],
            rowIndex: rowIndex,
            columnIndex: columnIndex,
          });
        }
      });

      // this.applyDisabled = false;

      // Clear selection after pasting
      this.selectedCells.clear();
    });

    // Clear the clipboard after pasting
    // navigator.clipboard.writeText('').then(
    //   () => {
    //     console.log('Clipboard cleared after paste.');
    //     this.copiedValue = '';
    //   },
    //   (error) => console.error('Failed to clear clipboard:', error)
    // );

    console.log('Pasted value into selected cells.');
  }

  handleAdditionalCapacityChange(rowIndex, columnIndex): void {
    // additional capacity = throughput * shifts
    // throughput = additional_capacity/shifts
    // throughput index = this.tableData[rowIndex]

    if (this.tableColumns[columnIndex].editable) {
      const columnKey = this.tableColumns[columnIndex].field;
      const updated_additional_capacity = this.tableData[rowIndex - 1][columnKey];
      const shifts = this.tableData[rowIndex + 1][columnKey];

      if (shifts === 0) {
        this.tableData[rowIndex - 1][columnKey] = 0;
      } else {
        this.tableData[rowIndex][columnKey] = shifts !== 0 ? updated_additional_capacity / shifts : 0; //throughput
        // Making Throughput Flag Dirty
        this.tableData[rowIndex][columnKey + '_dirty'] = true;
        this.tableData[rowIndex]['dirty'] = true;
        // Making Shifts Flag Dirty
        this.tableData[rowIndex + 1][columnKey + '_dirty'] = true;
        this.tableData[rowIndex + 1]['dirty'] = true;
        // Making Capex Flag Dirty
        this.tableData[rowIndex + 2][columnKey + '_dirty'] = true;
        this.tableData[rowIndex + 2]['dirty'] = true;
      }
    }
    this.applyDisabled = false;
  }

  handleThroughputChange(rowIndex, columnIndex): void {
    // additional capacity = throughput * shifts
    // additional capacity index = this.tableData[rowIndex-1]

    if (this.tableColumns[columnIndex].editable) {
      const columnKey = this.tableColumns[columnIndex].field;
      const updated_throughput = this.tableData[rowIndex - 1][columnKey];
      const shifts = this.tableData[rowIndex][columnKey];
      this.tableData[rowIndex - 2][columnKey] = updated_throughput * shifts; //additional capacity

      // Making additional capacity Flag Dirty
      this.tableData[rowIndex - 2][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 2]['dirty'] = true;

      // Making Shifts Flag Dirty
      this.tableData[rowIndex][columnKey + '_dirty'] = true;
      this.tableData[rowIndex]['dirty'] = true;

      // Making Capex Flag Dirty
      this.tableData[rowIndex + 1][columnKey + '_dirty'] = true;
      this.tableData[rowIndex + 1]['dirty'] = true;
    }
    this.applyDisabled = false;
  }

  handleShiftsChange(rowIndex, columnIndex): void {
    // additional capacity = throughput * shifts
    // additional capacity index = this.tableData[rowIndex-2]

    if (this.tableColumns[columnIndex].editable) {
      const columnKey = this.tableColumns[columnIndex].field;
      const updated_shifts = this.tableData[rowIndex - 1][columnKey];
      const throughput = this.tableData[rowIndex - 2][columnKey];
      this.tableData[rowIndex - 3][columnKey] = throughput * updated_shifts; //additional capacity
      // Making additional capacity Flag Dirty
      this.tableData[rowIndex - 3][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 3]['dirty'] = true;

      // Making Throughput Flag Dirty
      this.tableData[rowIndex - 2][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 2]['dirty'] = true;

      // Making Capex Flag Dirty
      this.tableData[rowIndex][columnKey + '_dirty'] = true;
      this.tableData[rowIndex]['dirty'] = true;
    }
    this.applyDisabled = false;
  }

  handleCapexChange(rowIndex, columnIndex): void {
    if (this.tableColumns[columnIndex].editable) {
      const columnKey = this.tableColumns[columnIndex].field;

      // Making additional capacity Flag Dirty
      this.tableData[rowIndex - 4][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 4]['dirty'] = true;

      // Making Throughput Flag Dirty
      this.tableData[rowIndex - 3][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 3]['dirty'] = true;

      // Making Shifts Flag Dirty
      this.tableData[rowIndex - 2][columnKey + '_dirty'] = true;
      this.tableData[rowIndex - 2]['dirty'] = true;
    }
    this.applyDisabled = false;
  }

  OnDeleteIntervention(intervention) {
    // this.selectedInterventions = [intervention.intervention_id];
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the Intervention <b> ${intervention.intervention_name} </b>? `,
      accept: () => {
        this.deleteIntervention.emit(intervention);
      },
      reject: (type: ConfirmEventType) => {
        console.log('rejected,type :>> ', type);
      },
    });
  }

  openEditIntervention(intervention) {
    this.editIntervention.emit(intervention);
  }

  onInterventionSelection(event, intervention) {
    this.tableData
      .filter((el) => el.intervention_id == intervention.intervention_id)
      .forEach((el) => {
        el.is_active = event.checked;
      });
    intervention['dirty'] = true;
    this.applyDisabled = false;
    this.getUpdatedRecords();
  }
}
